import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { postApi } from 'views/services/api';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Grid, Typography, Box, Paper, Fade, CircularProgress, Button } from '@mui/material';

// project imports
import AuthWrapper1 from '../AuthWrapper1';
import AuthCardWrapper from '../AuthCardWrapper';
import AnimateButton from 'ui-component/extended/AnimateButton';

// icons
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// ================================|| AUTH3 - VERIFY EMAIL ||================================ //

const VerifyEmail = () => {
  const theme = useTheme();
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await postApi('api/hotel/verify-email', { token });
        if (response.status === 200) {
          setStatus('success');
          setMessage(response.data?.message || 'Correo verificado exitosamente');
        } else {
          setStatus('error');
          setMessage(response.response?.data?.message || 'El enlace de verificación es inválido o ha expirado');
        }
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.message || 'Error al verificar el correo electrónico');
      }
    };

    if (token) {
      verifyToken();
    } else {
      setStatus('error');
      setMessage('Token de verificación no proporcionado');
    }
  }, [token]);

  const getStatusContent = () => {
    switch (status) {
      case 'loading':
        return {
          icon: <HourglassEmptyIcon sx={{ fontSize: 56, color: theme.palette.primary.main }} />,
          iconBg: theme.palette.primary.light + '20',
          iconBorder: theme.palette.primary.light + '50',
          title: 'Verificando...',
          subtitle: 'Estamos verificando tu correo electrónico, por favor espera un momento.'
        };
      case 'success':
        return {
          icon: <VerifiedOutlinedIcon sx={{ fontSize: 56, color: theme.palette.success.main }} />,
          iconBg: theme.palette.success.light + '20',
          iconBorder: theme.palette.success.light + '50',
          title: '¡Correo Verificado!',
          subtitle: 'Tu dirección de correo electrónico ha sido verificada exitosamente. Ya puedes iniciar sesión en el sistema.'
        };
      case 'error':
        return {
          icon: <ErrorOutlineIcon sx={{ fontSize: 56, color: theme.palette.error.main }} />,
          iconBg: theme.palette.error.light + '20',
          iconBorder: theme.palette.error.light + '50',
          title: 'Error de Verificación',
          subtitle: message
        };
      default:
        return {};
    }
  };

  const content = getStatusContent();

  return (
    <AuthWrapper1>
      <Grid
        container
        direction="column"
        justifyContent="flex-end"
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[300]} 100%)`,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 80%, ${theme.palette.primary.light}20 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, ${theme.palette.secondary.light}20 0%, transparent 50%)`,
            pointerEvents: 'none'
          }
        }}
      >
        <Grid item xs={12}>
          <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 'calc(100vh - 68px)' }}>
            <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
              <Fade in={true} timeout={800}>
                <Box>
                  <Paper
                    elevation={24}
                    sx={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      background: '#ffffff',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      position: 'relative',
                      maxWidth: 480,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                      }
                    }}
                  >
                    <AuthCardWrapper>
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        {/* Icon */}
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: content.iconBg,
                            borderRadius: '50%',
                            p: 3,
                            mb: 4,
                            border: '2px solid',
                            borderColor: content.iconBorder,
                            position: 'relative'
                          }}
                        >
                          {status === 'loading' ? (
                            <>
                              <CircularProgress
                                size={80}
                                thickness={2}
                                sx={{
                                  position: 'absolute',
                                  color: theme.palette.primary.light
                                }}
                              />
                              {content.icon}
                            </>
                          ) : (
                            content.icon
                          )}
                        </Box>

                        {/* Title */}
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.grey[800],
                            mb: 2,
                            letterSpacing: '-0.5px'
                          }}
                        >
                          {content.title}
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                          variant="body1"
                          sx={{
                            color: theme.palette.grey[600],
                            mb: 4,
                            lineHeight: 1.6,
                            px: 2
                          }}
                        >
                          {content.subtitle}
                        </Typography>

                        {/* Action Button */}
                        {status !== 'loading' && (
                          <AnimateButton>
                            <Button
                              component={Link}
                              to="/login"
                              variant="contained"
                              color="primary"
                              size="large"
                              sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                                px: 6,
                                py: 1.5,
                                boxShadow: 'none',
                                '&:hover': {
                                  backgroundColor: theme.palette.primary.dark,
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                }
                              }}
                            >
                              Ir al Inicio de Sesión
                            </Button>
                          </AnimateButton>
                        )}
                      </Box>
                    </AuthCardWrapper>
                  </Paper>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box
          sx={{
            py: 3,
            px: 2,
            textAlign: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            borderTop: '1px solid',
            borderColor: theme.palette.divider
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © {new Date().getFullYear()} PMS SegoCloud. Todos los derechos reservados.
          </Typography>
        </Box>
      </Grid>
    </AuthWrapper1>
  );
};

export default VerifyEmail;
